import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { fetchInsight, fetchInsights } from "../lib/api";

const formatDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return iso; }
};

const InsightDetail = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    setPost(null); setError(null);
    fetchInsight(slug).then(setPost).catch((e) => setError(e?.message || "Not found"));
    fetchInsights().then((all) => setRelated(all.filter((p) => p.slug !== slug).slice(0, 3)));
  }, [slug]);

  if (error) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-32 text-center" data-testid="insight-not-found">
        <p className="text-[11px] tracking-[0.32em] uppercase text-neutral-500">— 404</p>
        <h1 className="font-serif text-5xl mt-4">Insight not found</h1>
        <Link to="/insights" className="btn-editorial mt-10 inline-flex">Back to journal</Link>
      </div>
    );
  }

  if (!post) {
    return <div className="py-32 text-center text-neutral-500" data-testid="insight-loading">Loading…</div>;
  }

  return (
    <article data-testid="insight-detail">
      <section className="border-b border-[var(--rule)] py-20 lg:py-28">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12">
          <Reveal>
            <Link to="/insights" data-testid="back-to-insights" className="text-[11px] tracking-[0.28em] uppercase text-neutral-500 inline-flex items-center gap-2 mb-8 editorial-link">
              <ArrowLeft size={12} /> Back to journal
            </Link>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-wrap gap-4 text-[11px] tracking-[0.28em] uppercase text-neutral-500 mb-6">
              <span>{post.category}</span><span>·</span><span>{formatDate(post.date)}</span><span>·</span><span>{post.read_time}</span>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-[1.02]">
              {post.title}
            </h1>
          </Reveal>
          <Reveal delay={0.3}>
            <p className="font-serif text-xl lg:text-2xl italic text-neutral-700 mt-8 max-w-3xl leading-snug">
              {post.excerpt}
            </p>
          </Reveal>
          <Reveal delay={0.4}>
            <p className="text-[11px] tracking-[0.28em] uppercase text-neutral-500 mt-10">— By {post.author}</p>
          </Reveal>
        </div>
      </section>

      {post.image && (
        <section className="border-b border-[var(--rule)]">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
            <Reveal>
              <div className="img-frame aspect-[16/8] border border-[var(--rule)]">
                <img src={post.image} alt={post.title} className="w-full h-full object-cover grayscale" />
              </div>
            </Reveal>
          </div>
        </section>
      )}

      <section className="py-20 lg:py-28">
        <div className="max-w-[760px] mx-auto px-6 md:px-12">
          {post.content.split("\n\n").map((para, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <p className={`mb-7 leading-[1.85] text-neutral-800 ${i === 0 ? "first-letter:font-serif first-letter:text-7xl first-letter:font-light first-letter:float-left first-letter:mr-3 first-letter:leading-[0.85]" : ""}`}>
                {para}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="border-t border-neutral-900 py-20">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <p className="text-[11px] tracking-[0.32em] uppercase text-neutral-500 mb-10">— Continue reading</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              {related.map((p) => (
                <Link
                  key={p.id}
                  to={`/insights/${p.slug}`}
                  data-testid={`related-${p.slug}`}
                  className="group border-t border-neutral-900 pt-6 block"
                >
                  <div className="flex gap-3 text-[10px] tracking-[0.28em] uppercase text-neutral-500 mb-3">
                    <span>{p.category}</span><span>·</span><span>{formatDate(p.date)}</span>
                  </div>
                  <h4 className="font-serif text-2xl font-light leading-snug group-hover:italic transition-all">{p.title}</h4>
                  <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-neutral-500 mt-4">
                    Read <ArrowUpRight size={12} />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
};

export default InsightDetail;
